// Shared presentational primitives, pure formatters, and constants used across
// the account experience's tab modules. No host coupling; brand-token vars only.
import type { ReactNode } from 'react'
import { ChevR, ChevBackL } from './icons'
import type { Visit, PortalSubscription } from './types'

export const URGENT_MIN = 30 // a live session with this many minutes left flips to the attention state

export const HELP_URL = 'https://parkify.com/help'
export const TERMS_URL = 'https://parkify.com/articles/terms'
export const PRIVACY_URL = 'https://parkify.com/articles/privacy'
// Avery's live support line — same number the SupportButton uses across surfaces.
export const SUPPORT_E164 = '+18559175072'
export const SUPPORT_PRETTY = '1 (855) 917-5072'

// ── small presentational helpers ────────────────────────────────────────────

export function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

// Pretty display for a US number (+15551234567 → (555) 123-4567); anything
// non-US (or null/empty) is returned as-is. Shared across every host (portal,
// consumer checkout) so the phone display format never drifts between them.
export function formatPhoneDisplay(e164: string | null | undefined): string {
  if (!e164) return ''
  // Also accepts a bare 10-digit or 1-prefixed US number, not just E.164. The
  // phone-change flow shows the number the parker just TYPED (it isn't E.164
  // until the server normalizes it), so the strict version fell through and
  // printed "5557654321" in a sentence every other surface renders as
  // "(555) 765-4321". Anything it still can't parse is returned untouched, so
  // this only ever improves a case that was previously raw.
  const digits = e164.replace(/[^\d+]/g, '')
  const m = /^(?:\+?1)?(\d{3})(\d{3})(\d{4})$/.exec(digits)
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164
}

// First + last initial from a saved name ("Austin Taylor" → "AT"; a single name
// → its first two letters). Returns null when no name is saved, so the avatar
// falls back to the Parkify widget mark rather than guessing initials from an
// email or phone number.
export function initialsOf(name: string | null): string | null {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return null
}

// `YYYY-MM-DD` value for a <input type="date"> (min = today, default = +7 days).
export function todayDateValue(): string {
  return new Date().toISOString().slice(0, 10)
}
export function defaultRemoveDate(): string {
  return new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
}
// A temp plate's removal date for display, e.g. "Mon, Jul 6".
export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

// Stripe brand slugs → display labels. Falls back to a capitalized slug.
export function formatCardBrand(brand: string | null): string {
  if (!brand) return 'Card'
  const map: Record<string, string> = {
    visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', 'american express': 'Amex',
    discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay',
  }
  return map[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1)
}

// Whole-dollar plans render clean ("$150"); a plan with cents keeps them ("$149.50").
export function monthlyPrice(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`
}
export function renewalDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
export function statusLabel(status: PortalSubscription['status']): string {
  if (status === 'past_due') return 'Past due'
  if (status === 'trialing') return 'Trial'
  return 'Active'
}

export function formatRange(startISO: string | null, endISO: string | null): string {
  if (!startISO) return '—'
  const start = new Date(startISO)
  const startStr = start.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
  if (!endISO) return `${startStr} · in progress`
  const end = new Date(endISO)
  const sameDay = start.toDateString() === end.toDateString()
  const endStr = sameDay
    ? end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
    : end.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  return `${startStr} → ${endStr}`
}

export function formatDuration(minutes: number | null): string {
  if (minutes == null) return ''
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (m === 0) return `${h} hr`
  return `${h} hr ${m} min`
}

export function formatAmount(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`
}

/**
 * Offline fallback estimate for the add-time charge, derived from what this
 * session has cost so far. Used ONLY when the exact server quote can't load;
 * the charge itself is always server-authoritative. Returns null when there
 * isn't enough data to estimate.
 */
export function estimateExtendUsd(visit: Visit, minutes: number): number | null {
  const basisCents = visit.gross_cents || visit.amount_cents
  const parkedMinutes = visit.duration_minutes
  if (!basisCents || basisCents <= 0 || !parkedMinutes || parkedMinutes <= 0) return null
  const perMinuteCents = basisCents / parkedMinutes
  let cents = Math.ceil(perMinuteCents * minutes)
  if (cents > 0 && cents < 50) cents = 50
  return cents / 100
}

export function maskPhone(phone: string): string {
  const digits = phone.replace(/[^\d]/g, '')
  if (digits.length < 4) return phone
  return '••••' + digits.slice(-4)
}

/** A single tappable list row (icon + title + optional sub + chevron). */
export function Row({
  icon, title, sub, badge, danger, last, onClick, href,
}: {
  icon: ReactNode
  title: string
  sub?: string
  badge?: string
  danger?: boolean
  last?: boolean
  onClick?: () => void
  href?: string
}) {
  const inner = (
    <>
      <span className={`pka-row__icon${danger ? ' pka-row__icon--danger' : ''}`}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 14, fontWeight: 700,
          color: danger ? 'var(--color-error)' : 'var(--color-black)',
        }}>
          <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          {badge && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
              color: 'var(--color-success)', background: 'var(--color-success-bg)',
              padding: '2px 7px', borderRadius: 999,
            }}>{badge}</span>
          )}
        </span>
        {sub && (
          <span style={{
            display: 'block', fontSize: 12, fontWeight: 500,
            color: 'var(--color-gray-600)', marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{sub}</span>
        )}
      </span>
      {!danger && (href || onClick) && <ChevR />}
    </>
  )
  const className = `pka-row${last ? ' pka-row--last' : ''}`
  if (href) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        {inner}
      </a>
    )
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {inner}
    </button>
  )
}

/** Sub-screen back affordance (mirrored chevron + "Back"), matching the Permit
 *  detail view. Sub-screens are local view-stack state in the account panel, not
 *  routes — this just resets that state. */
export function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" onClick={onBack} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-brand)',
      fontWeight: 700, fontSize: 13, paddingBottom: 16,
    }}>
      <ChevBackL /> Back
    </button>
  )
}

export function SecLbl({ children, action, onAction }: { children: ReactNode; action?: string; onAction?: () => void }) {
  return (
    <div className="pka-seclbl">
      <span>{children}</span>
      {action && <button type="button" onClick={onAction}>+ {action}</button>}
    </div>
  )
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2,
        color: 'var(--color-gray-700)', marginBottom: 6,
      }}>{label}</div>
      {children}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p style={{
      margin: 0, color: 'var(--color-error)', background: 'var(--color-error-bg)',
      border: '1px solid rgba(237, 28, 36, 0.2)', borderRadius: 'var(--radius-sm)',
      padding: '8px 12px', fontSize: 12,
    }}>{children}</p>
  )
}
